import { useEffect, useState } from "react";
import { api, leerSesion, ErrorApi } from "../api.ts";
import { tamanoLegible, aLocal } from "../../../dominio/src/index.ts";
import { AvisoError, CampoClave, Seccion, useAccion } from "./comun.tsx";
import { CampoNumero } from "../componentes/CampoNumero.tsx";

interface Item { id: string; archivo: string; origen: string; estado: string; inicio: string; fin: string | null; bytes: number | null; verificado: boolean; local: boolean; drive: string; driveError: string | null; error: string | null; otraClave: boolean }
interface Estado {
  activo: boolean; hora: string; retencion: { diarios: number; semanales: number; mensuales: number }; tieneClave: boolean; pista: string; carpeta: string;
  google: { disponible: boolean; conectado: boolean; email: string; desde: string | null };
  ultimoOk: string | null; proximo: string | null; alerta: string | null; trabajando: string | null;
  respaldos: Item[]; soloDrive: { id: string; archivo: string; bytes: number; fecha: string }[];
  eventos: { respaldo: string; accion: string; resultado: string; detalle: Record<string, unknown>; creado: string }[];
}

const cuando = (iso: string | null) => {
  if (!iso) return "—";
  const l = aLocal(new Date(iso));
  const [a, m, d] = l.fecha.split("-");
  return `${d}/${m}/${a} ${l.hora}`;
};
const pl = (n: number | unknown, uno: string, varios: string) => `${n} ${Number(n) === 1 ? uno : varios}`;
const ORIGEN: Record<string, string> = { programado: "Automático", manual: "Manual", antes_de_restaurar: "Antes de restaurar" };

export function Respaldos() {
  const [e, setE] = useState<Estado | null>(null);
  const [msg, setMsg] = useState<{ tipo: "ok" | "error" | "info"; texto: string } | null>(null);
  const [accion, setAccion] = useState<{ tipo: "probar" | "restaurar"; id: string; archivo: string } | null>(null);
  const a = useAccion();

  const cargar = async () => { const r = await a.correr(() => api<Estado>("GET", "/api/respaldos")); if (r) setE(r); return r; };
  useEffect(() => {
    cargar();
    const g = new URLSearchParams(location.hash.split("?")[1] ?? "").get("google");
    if (g) {
      setMsg(g === "ok" ? { tipo: "ok", texto: "Google Drive quedó conectado. Los respaldos se subirán a la carpeta «Donna · Respaldos» de tu Drive." }
        : g === "cancelado" ? { tipo: "info", texto: "Cancelaste la conexión con Google Drive." }
        : { tipo: "error", texto: "No se pudo conectar Google Drive. Inténtalo de nuevo." });
      history.replaceState(null, "", "#/respaldos");
    }
  }, []);
  // mientras hay un respaldo en curso, se actualiza cada 2 segundos
  useEffect(() => {
    if (!e?.trabajando && !e?.respaldos.some((x) => x.estado === "en_curso")) return;
    const t = setTimeout(cargar, 2000);
    return () => clearTimeout(t);
  }, [e]);

  if (!e) return <Seccion titulo="Respaldos"><AvisoError error={a.error} />{!a.error && <p className="muted">Cargando…</p>}</Seccion>;

  async function respaldarAhora() {
    setMsg(null);
    if (await a.correr(() => api("POST", "/api/respaldos"))) { setMsg({ tipo: "info", texto: "Respaldo en curso. Puedes seguir trabajando." }); await cargar(); }
  }
  async function descargar(x: Item) {
    const s = leerSesion();
    await a.correr(async () => {
      const r = await fetch(`/api/respaldos/${x.id}/descargar`, { headers: s ? { authorization: `Bearer ${s.token}` } : {} });
      if (!r.ok) throw new ErrorApi(r.status, ((await r.json().catch(() => ({}))) as { error?: string }).error ?? "No se pudo descargar.");
      const url = URL.createObjectURL(await r.blob());
      const enlace = document.createElement("a"); enlace.href = url; enlace.download = x.archivo; enlace.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    });
  }
  async function borrar(x: Item) {
    if (!confirm(`¿Borrar el respaldo del ${cuando(x.inicio)}? Se borra de la computadora${x.drive === "subido" ? " y de Google Drive" : ""}.`)) return;
    const r = await a.correr(() => api<Estado>("DELETE", `/api/respaldos/${x.id}`));
    if (r) setE({ ...e!, ...r });
  }
  async function subir(x: Item) {
    const r = await a.correr(() => api<Estado>("POST", `/api/respaldos/${x.id}/drive`));
    if (r) setE({ ...e!, ...r });
  }

  const ultimo = e.respaldos.find((x) => x.estado === "ok");
  return (
    <div className="respaldos">
      {msg && <div className={`aviso ${msg.tipo}`} role="status">{msg.texto}</div>}
      {e.alerta && <div className="aviso error" role="alert">{e.alerta}</div>}
      <AvisoError error={a.error} />

      <Seccion titulo="Respaldos" acciones={<button className="btn pri" onClick={respaldarAhora} disabled={a.ocupado || !!e.trabajando || !e.tieneClave}>{e.trabajando ? `En curso: ${e.trabajando}…` : "Respaldar ahora"}</button>}>
        <div className="resumen-respaldo">
          <div><span className="muted">Último respaldo correcto</span><b>{cuando(e.ultimoOk)}</b>{ultimo && <small className="muted">{tamanoLegible(ultimo.bytes ?? 0)} · {ultimo.verificado ? "verificado" : "sin verificar"}</small>}</div>
          <div><span className="muted">Próximo automático</span><b>{e.activo && e.proximo ? (new Date(e.proximo).getTime() - Date.now() < 60000 ? "En los próximos minutos" : cuando(e.proximo)) : "Desactivado"}</b><small className="muted">todos los días a las {e.hora}</small></div>
          <div><span className="muted">En la computadora</span><b className="mono" style={{ fontSize: 13, wordBreak: "break-all" }}>{e.carpeta}</b></div>
          <div><span className="muted">En Google Drive</span><b>{e.google.conectado ? e.google.email || "Conectado" : "No conectado"}</b>{e.google.conectado && <small className="muted">carpeta «Donna · Respaldos»</small>}</div>
        </div>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Cada respaldo contiene toda la información de Donna (pacientes, historias, odontogramas, fotos, citas, pagos y documentos firmados),
          va cifrado con tu clave de respaldo y se verifica al crearlo. Si la computadora está apagada a la hora programada, el respaldo se hace apenas se encienda.
        </p>
      </Seccion>

      <div className="dos">
        <Clave e={e} alGuardar={(r) => { setE({ ...e, ...r }); setMsg({ tipo: "ok", texto: "Clave de respaldo guardada. Anótala en un lugar seguro, fuera de esta computadora." }); }} />
        <Drive e={e} alCambiar={(r) => setE({ ...e, ...r })} />
      </div>

      <Programacion e={e} alGuardar={(r) => { setE({ ...e, ...r }); setMsg({ tipo: "ok", texto: "Programación guardada." }); }} />

      <Seccion titulo="Copias guardadas">
        {!e.respaldos.length && !e.soloDrive.length ? <p className="muted">Todavía no hay respaldos.</p> : (
          <div className="tabla-scroll">
            <table className="tabla-resp" aria-label="Copias guardadas">
              <thead><tr><th>Fecha</th><th>Tipo</th><th>Tamaño</th><th>Estado</th><th>Dónde está</th><th></th></tr></thead>
              <tbody>
                {e.respaldos.map((x) => (
                  <tr key={x.id}>
                    <td className="mono">{cuando(x.inicio)}</td>
                    <td>{ORIGEN[x.origen] ?? x.origen}</td>
                    <td>{x.bytes ? tamanoLegible(x.bytes) : "—"}</td>
                    <td>
                      {x.estado === "ok" ? <span className="chip e-confirmada">{x.verificado ? "Verificado" : "Correcto"}</span>
                        : x.estado === "en_curso" ? <span className="chip">En curso…</span>
                        : <span className="chip rojo" title={x.error ?? ""}>Falló</span>}
                      {x.otraClave && <small className="muted"> · clave anterior</small>}
                      {x.estado === "error" && x.error && <div className="muted" style={{ fontSize: 12 }}>{x.error}</div>}
                    </td>
                    <td>
                      {x.local && <span className="chip">Computadora</span>}{" "}
                      {x.drive === "subido" && <span className="chip azul">Drive</span>}
                      {x.drive === "error" && <span className="chip rojo" title={x.driveError ?? ""}>Drive: falló</span>}
                      {x.drive === "pendiente" && <span className="chip">Drive: pendiente</span>}
                    </td>
                    <td className="acciones-fila">
                      {x.estado === "ok" && <>
                        {x.local && <button className="btn chico" onClick={() => descargar(x)}>Descargar</button>}
                        <button className="btn chico" onClick={() => setAccion({ tipo: "probar", id: x.id, archivo: x.archivo })}>Probar</button>
                        <button className="btn chico peligro" onClick={() => setAccion({ tipo: "restaurar", id: x.id, archivo: x.archivo })}>Restaurar</button>
                        {e.google.conectado && x.local && (x.drive === "error" || x.drive === "pendiente") && <button className="btn chico" onClick={() => subir(x)}>Subir a Drive</button>}
                      </>}
                      {x.estado !== "en_curso" && <button className="btn chico" aria-label="Borrar respaldo" onClick={() => borrar(x)}>Borrar</button>}
                    </td>
                  </tr>
                ))}
                {e.soloDrive.map((x) => (
                  <tr key={x.id}>
                    <td className="mono">{cuando(x.fecha)}</td><td>Solo en Drive</td><td>{tamanoLegible(x.bytes)}</td><td><span className="chip">Sin registro local</span></td>
                    <td><span className="chip azul">Drive</span></td>
                    <td className="acciones-fila">
                      <button className="btn chico" onClick={() => setAccion({ tipo: "probar", id: x.id, archivo: x.archivo })}>Probar</button>
                      <button className="btn chico peligro" onClick={() => setAccion({ tipo: "restaurar", id: x.id, archivo: x.archivo })}>Restaurar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {accion && <Accion accion={accion} alCerrar={() => setAccion(null)} alTerminar={(texto) => { setAccion(null); setMsg({ tipo: "ok", texto }); cargar(); }} />}
      </Seccion>

      {e.eventos.length > 0 && (
        <Seccion titulo="Pruebas y restauraciones">
          <ul className="preguntas">
            {e.eventos.map((v, i) => (
              <li key={i}>
                <span className="mono">{cuando(v.creado)}</span> · {v.accion === "prueba" ? "Prueba de restauración" : "Restauración"} de {v.respaldo.startsWith("drive:") ? "un respaldo de Drive" : v.respaldo}:{" "}
                {v.resultado === "ok" ? <b>correcta ({pl(v.detalle.pacientes, "paciente", "pacientes")}, {pl(v.detalle.citas, "cita", "citas")}, {pl(v.detalle.fotos, "foto", "fotos")})</b> : <span style={{ color: "var(--red)" }}>falló: {String(v.detalle.error ?? "")}</span>}
              </li>
            ))}
          </ul>
        </Seccion>
      )}
    </div>
  );
}

function Clave({ e, alGuardar }: { e: Estado; alGuardar: (r: Estado) => void }) {
  const [abierto, setAbierto] = useState(!e.tieneClave);
  const [clave, setClave] = useState(""), [conf, setConf] = useState(""), [pista, setPista] = useState(e.pista);
  const [anotada, setAnotada] = useState(false);
  const a = useAccion();
  async function guardar() {
    const r = await a.correr(() => api<Estado>("PUT", "/api/respaldos/clave", { clave, confirmacion: conf, pista }));
    if (r) { alGuardar(r); setAbierto(false); setClave(""); setConf(""); setAnotada(false); }
  }
  return (
    <Seccion titulo="Clave de respaldo">
      {e.tieneClave && !abierto ? (
        <>
          <p style={{ margin: 0 }}>✔ Definida.{e.pista && <span className="muted"> Pista: {e.pista}</span>}</p>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>Se necesita para restaurar un respaldo en otra computadora. Donna la guarda cifrada para hacer los respaldos automáticos.</p>
          <div><button className="btn chico" onClick={() => setAbierto(true)}>Cambiar clave</button></div>
        </>
      ) : (
        <>
          <div className="aviso info" style={{ fontSize: 13 }}>
            <b>Importante:</b> los respaldos se cifran con esta clave. <b>Sin ella no se pueden restaurar</b>, ni siquiera nosotros podemos recuperarla.
            Anótala en papel y guárdala fuera del consultorio.
            {e.tieneClave && " Los respaldos anteriores seguirán necesitando la clave anterior."}
          </div>
          <CampoClave etiqueta="Clave (12 caracteres o más)" autoComplete="new-password" value={clave} onChange={setClave} />
          <CampoClave etiqueta="Repite la clave" autoComplete="new-password" value={conf} onChange={setConf} />
          <label className="campo">Pista (opcional, sin escribir la clave)<input className="entrada" value={pista} maxLength={80} onChange={(x) => setPista(x.target.value)} /></label>
          <label className="fila" style={{ gap: 8, fontSize: 14 }}><input type="checkbox" checked={anotada} onChange={() => setAnotada(!anotada)} /> Ya anoté la clave en un lugar seguro</label>
          <AvisoError error={a.error} />
          <div className="fila">
            <button className="btn pri" onClick={guardar} disabled={a.ocupado || !anotada || !clave}>Guardar clave</button>
            {e.tieneClave && <button className="btn" onClick={() => setAbierto(false)}>Cancelar</button>}
          </div>
        </>
      )}
    </Seccion>
  );
}

function Drive({ e, alCambiar }: { e: Estado; alCambiar: (r: Estado) => void }) {
  const a = useAccion();
  async function conectar() {
    const r = await a.correr(() => api<{ url: string }>("POST", "/api/respaldos/google/conectar"));
    if (r) location.href = r.url;
  }
  async function desconectar() {
    if (!confirm("¿Desconectar Google Drive? Los respaldos que ya están en tu Drive se quedan ahí; los nuevos solo se guardarán en la computadora.")) return;
    const r = await a.correr(() => api<Estado>("POST", "/api/respaldos/google/desconectar"));
    if (r) alCambiar(r);
  }
  return (
    <Seccion titulo="Google Drive">
      {!e.google.disponible ? (
        <>
          <p style={{ margin: 0 }}>Para subir los respaldos a Google Drive, esta instalación necesita las credenciales de Google (GOOGLE_CLIENT_ID y GOOGLE_CLIENT_SECRET en el archivo .env).</p>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>La guía paso a paso está en el README, sección «Respaldos en Google Drive». Otra opción sin credenciales: instala Google Drive para escritorio y apunta la carpeta de respaldos a tu unidad de Drive (RESPALDOS_CARPETA en .env).</p>
        </>
      ) : e.google.conectado ? (
        <>
          <p style={{ margin: 0 }}>✔ Conectado con <b>{e.google.email || "tu cuenta de Google"}</b>{e.google.desde && <span className="muted"> desde el {cuando(e.google.desde).slice(0, 10)}</span>}.</p>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>Cada respaldo se sube a la carpeta «Donna · Respaldos». Donna solo tiene permiso sobre los archivos que ella crea: no puede ver el resto de tu Drive.</p>
          <div><button className="btn chico" onClick={desconectar} disabled={a.ocupado}>Desconectar</button></div>
        </>
      ) : (
        <>
          <p style={{ margin: 0 }}>Guarda una copia de cada respaldo fuera del consultorio: si la computadora se daña o se pierde, tus datos siguen a salvo.</p>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>Donna solo pedirá permiso para manejar los archivos que ella crea en tu Drive. Los respaldos van cifrados: Google no puede leerlos.</p>
          <div><button className="btn pri" onClick={conectar} disabled={a.ocupado}>Conectar Google Drive</button></div>
        </>
      )}
      <AvisoError error={a.error} />
    </Seccion>
  );
}

function Programacion({ e, alGuardar }: { e: Estado; alGuardar: (r: Estado) => void }) {
  const [activo, setActivo] = useState(e.activo), [hora, setHora] = useState(e.hora), [ret, setRet] = useState(e.retencion);
  const a = useAccion();
  const n = (k: keyof Estado["retencion"]) => (
    <CampoNumero className="entrada" min={k === "diarios" ? 1 : 0} max={60} valor={ret[k]} alCambiar={(n) => setRet({ ...ret, [k]: n })} />
  );
  async function guardar() {
    const r = await a.correr(() => api<Estado>("PUT", "/api/respaldos/config", { activo, hora, retencion: ret }));
    if (r) alGuardar(r);
  }
  return (
    <Seccion titulo="Programación y copias que se conservan" acciones={<button className="btn pri" onClick={guardar} disabled={a.ocupado}>Guardar</button>}>
      <div className="rejilla">
        <label className="fila" style={{ gap: 8 }}><input type="checkbox" checked={activo} onChange={() => setActivo(!activo)} /> Respaldo automático diario</label>
        <label className="campo">Hora<input className="entrada" type="time" value={hora} onChange={(x) => setHora(x.target.value)} /></label>
        <label className="campo">Copias diarias{n("diarios")}</label>
        <label className="campo">Copias semanales{n("semanales")}</label>
        <label className="campo">Copias mensuales{n("mensuales")}</label>
      </div>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        Con {ret.diarios} diarias, {ret.semanales} semanales y {ret.mensuales} mensuales puedes volver a cualquiera de los últimos {ret.diarios} días,
        y a un punto de cada semana y cada mes hasta {ret.mensuales} meses atrás. Las copias más viejas se borran solas (también en Drive).
      </p>
      <AvisoError error={a.error} />
    </Seccion>
  );
}

function Accion({ accion, alCerrar, alTerminar }: { accion: { tipo: "probar" | "restaurar"; id: string; archivo: string }; alCerrar: () => void; alTerminar: (texto: string) => void }) {
  const [clave, setClave] = useState(""), [conf, setConf] = useState("");
  const a = useAccion();
  const restaurar = accion.tipo === "restaurar";
  async function ejecutar() {
    const r = await a.correr(() => api<{ pacientes: number; citas: number; fotos: number; creado: string }>("POST", `/api/respaldos/${encodeURIComponent(accion.id)}/${accion.tipo}`, { clave, confirmacion: conf }));
    if (!r) return;
    if (restaurar) {
      alTerminar(`Base restaurada al ${cuando(r.creado)}: ${pl(r.pacientes, "paciente", "pacientes")}, ${pl(r.citas, "cita", "citas")} y ${pl(r.fotos, "foto", "fotos")}. Antes se guardó un respaldo del estado anterior. La página se recargará.`);
      setTimeout(() => location.reload(), 4000);
    } else alTerminar(`Prueba correcta: el respaldo del ${cuando(r.creado)} se restauró en una base temporal con ${pl(r.pacientes, "paciente", "pacientes")}, ${pl(r.citas, "cita", "citas")} y ${pl(r.fotos, "foto", "fotos")}. Sirve.`);
  }
  return (
    <div className={`panel-accion${restaurar ? " peligro" : ""}`} role="dialog" aria-label={restaurar ? "Restaurar respaldo" : "Probar respaldo"}>
      <h3 style={{ margin: 0 }}>{restaurar ? "Restaurar este respaldo" : "Probar este respaldo"}</h3>
      <p className="mono muted" style={{ margin: 0, fontSize: 12 }}>{accion.archivo}</p>
      {restaurar ? (
        <div className="aviso error" style={{ fontSize: 14 }}>
          Toda la información de Donna volverá a como estaba en ese momento: <b>lo registrado después se perderá</b>.
          Antes de restaurar, Donna guardará un respaldo del estado actual. Durante la restauración nadie podrá usar Donna (unos minutos).
        </div>
      ) : (
        <p style={{ margin: 0, fontSize: 14 }}>Donna restaura el respaldo en una base temporal, comprueba lo que contiene y la borra. Tus datos actuales no se tocan.</p>
      )}
      <CampoClave etiqueta={<>Clave de respaldo <span className="muted" style={{ fontWeight: 400 }}>(déjala vacía para usar la clave guardada)</span></>} autoComplete="off" value={clave} onChange={setClave} />
      {restaurar && <label className="campo">Escribe RESTAURAR para confirmar<input className="entrada" value={conf} onChange={(x) => setConf(x.target.value)} /></label>}
      <AvisoError error={a.error} />
      <div className="fila">
        <button className={`btn ${restaurar ? "peligro" : "pri"}`} onClick={ejecutar} disabled={a.ocupado || (restaurar && conf !== "RESTAURAR")}>
          {a.ocupado ? (restaurar ? "Restaurando…" : "Probando…") : restaurar ? "Restaurar" : "Probar restauración"}
        </button>
        <button className="btn" onClick={alCerrar} disabled={a.ocupado}>Cancelar</button>
      </div>
    </div>
  );
}

/** Aviso en la parte superior para quien administra los respaldos. */
export function AvisoRespaldos({ ir }: { ir: (r: string) => void }) {
  const [alerta, setAlerta] = useState<string | null>(null);
  useEffect(() => { api<{ alerta: string | null }>("GET", "/api/respaldos/alerta").then((r) => setAlerta(r.alerta)).catch(() => undefined); }, [location.hash.startsWith("#/respaldos")]);
  if (!alerta || location.hash.startsWith("#/respaldos")) return null;
  return <div className="aviso info banda" role="status">⚠ {alerta} <button className="enlace" onClick={() => ir("respaldos")}>Ir a respaldos</button></div>;
}
